import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { FolderOpen, Plus, Pencil, Trash2, X, Image, Video, Upload, ArrowLeft, Play, Link2 } from "lucide-react";
import { toast } from "sonner";
import { API_URL as API } from "../../config";
import { uploadFile } from "../../utils/chunkedUpload";

function AlbumForm({ album, onSave, onCancel, lang }) {
  const [form, setForm] = useState({ title: album?.title || "", description: album?.description || "", cover_image: album?.cover_image || "" });
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-bold font-['Kanit']">{album ? (lang === "es" ? "Editar Album" : "Edit Album") : (lang === "es" ? "Nuevo Album" : "New Album")}</h3>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="p-5 space-y-4" data-testid="album-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Titulo" : "Title"} *</label>
            <input required value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="album-title-input"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Descripcion" : "Description"}</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB] resize-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Imagen de Portada URL" : "Cover Image URL"}</label>
            <input value={form.cover_image} onChange={e => setForm({ ...form, cover_image: e.target.value })} placeholder="https://..."
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">
              {lang === "es" ? "Cancelar" : "Cancel"}
            </button>
            <button type="submit" data-testid="album-save-btn" className="flex-1 bg-[#87CEEB] text-black px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#6bb8d9]">
              {album ? (lang === "es" ? "Actualizar" : "Update") : (lang === "es" ? "Crear" : "Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MediaForm({ media, albumId, onSave, onCancel, lang, authFetch }) {
  const [form, setForm] = useState({
    type: media?.type || "image", title: media?.title || "", description: media?.description || "",
    url: media?.url || "", youtube_url: media?.youtube_url || "", album_id: media?.album_id || albumId || "",
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileUpload = async (file) => {
    setUploading(true);
    setUploadProgress(0);
    try {
      const data = await uploadFile(file, authFetch, (pct) => setUploadProgress(pct));
      setForm(prev => ({ ...prev, url: data.path }));
      toast.success(lang === "es" ? "Archivo subido" : "File uploaded");
    } catch (err) {
      toast.error(lang === "es" ? "Error al subir" : (err.message || "Upload failed"));
    }
    setUploading(false);
    setUploadProgress(0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="text-lg font-bold font-['Kanit']">{media ? (lang === "es" ? "Editar Media" : "Edit Media") : (lang === "es" ? "Agregar Media" : "Add Media")}</h3>
          <button onClick={onCancel} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); onSave(form); }} className="p-5 space-y-4" data-testid="media-form">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{lang === "es" ? "Tipo" : "Type"}</label>
            <div className="flex gap-2">
              {["image", "video"].map(t => (
                <button key={t} type="button" onClick={() => setForm({ ...form, type: t })}
                  className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${form.type === t ? 'bg-[#87CEEB] text-black' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {t === "image" ? <Image className="w-4 h-4" /> : <Video className="w-4 h-4" />}
                  {t === "image" ? (lang === "es" ? "Imagen" : "Image") : "Video"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Titulo" : "Title"}</label>
            <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="media-title-input"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
          </div>
          {form.type === "video" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
              <input value={form.youtube_url} onChange={e => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=..."
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {form.type === "image" ? (lang === "es" ? "URL de Imagen o Subir" : "Image URL or Upload") : (lang === "es" ? "URL de Video o Subir" : "Video URL or Upload")}
            </label>
            <div className="flex gap-2">
              <input value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://..."
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB]" />
              <label className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-medium cursor-pointer ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-[#87CEEB]/10 text-[#87CEEB] hover:bg-[#87CEEB]/20'}`}>
                <Upload className="w-4 h-4" /> {uploading ? `${uploadProgress}%` : (lang === "es" ? "Subir" : "Upload")}
                <input type="file" accept={form.type === "image" ? "image/*" : "video/*"} className="hidden" disabled={uploading}
                  onChange={e => e.target.files[0] && handleFileUpload(e.target.files[0])} />
              </label>
            </div>
            {form.url && form.type === "image" && <img src={form.url} alt="" className="h-20 mt-2 rounded-lg object-cover border" />}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{lang === "es" ? "Descripcion" : "Description"}</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:border-[#87CEEB] resize-none" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onCancel} className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium hover:bg-gray-50">
              {lang === "es" ? "Cancelar" : "Cancel"}
            </button>
            <button type="submit" data-testid="media-save-btn" className="flex-1 bg-[#87CEEB] text-black px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#6bb8d9]">
              {media ? (lang === "es" ? "Actualizar" : "Update") : (lang === "es" ? "Agregar" : "Add")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function GalleryManagerPage() {
  const { authFetch } = useAuth();
  const { lang } = useLanguage();
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAlbumForm, setShowAlbumForm] = useState(false);
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [showMediaForm, setShowMediaForm] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);

  const loadAlbums = useCallback(async () => {
    const res = await authFetch(`${API}/api/admin/gallery/albums`);
    if (res.ok) setAlbums(await res.json());
    setLoading(false);
  }, [authFetch]);

  const loadMedia = useCallback(async (albumId) => {
    const q = albumId ? `?album_id=${albumId}` : "";
    const res = await authFetch(`${API}/api/admin/gallery/media${q}`);
    if (res.ok) setMedia(await res.json());
  }, [authFetch]);

  useEffect(() => { loadAlbums(); }, [loadAlbums]);
  useEffect(() => { if (selectedAlbum) loadMedia(selectedAlbum.id); }, [selectedAlbum, loadMedia]);

  const saveAlbum = async (data) => {
    const url = editingAlbum ? `${API}/api/admin/gallery/albums/${editingAlbum.id}` : `${API}/api/admin/gallery/albums`;
    const res = await authFetch(url, { method: editingAlbum ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    if (res.ok) {
      toast.success(editingAlbum ? (lang === "es" ? "Album actualizado" : "Album updated") : (lang === "es" ? "Album creado" : "Album created"));
      setShowAlbumForm(false);
      setEditingAlbum(null);
      loadAlbums();
    }
  };

  const deleteAlbum = async (id) => {
    if (!window.confirm(lang === "es" ? "Eliminar este album y todo su contenido?" : "Delete this album and all its media?")) return;
    const res = await authFetch(`${API}/api/admin/gallery/albums/${id}`, { method: "DELETE" });
    if (res.ok) {
      toast.success(lang === "es" ? "Album eliminado" : "Album deleted");
      if (selectedAlbum?.id === id) setSelectedAlbum(null);
      loadAlbums();
    }
  };

  const saveMedia = async (data) => {
    if (selectedAlbum) data.album_id = selectedAlbum.id;
    const url = editingMedia ? `${API}/api/admin/gallery/media/${editingMedia.id}` : `${API}/api/admin/gallery/media`;
    const res = await authFetch(url, { method: editingMedia ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
    if (res.ok) {
      toast.success(editingMedia ? (lang === "es" ? "Media actualizado" : "Media updated") : (lang === "es" ? "Media agregado" : "Media added"));
      setShowMediaForm(false);
      setEditingMedia(null);
      if (selectedAlbum) loadMedia(selectedAlbum.id);
    }
  };

  const deleteMedia = async (id) => {
    if (!window.confirm(lang === "es" ? "Eliminar?" : "Delete this item?")) return;
    const res = await authFetch(`${API}/api/admin/gallery/media/${id}`, { method: "DELETE" });
    if (res.ok) {
      toast.success(lang === "es" ? "Eliminado" : "Deleted");
      if (selectedAlbum) loadMedia(selectedAlbum.id);
    }
  };

  if (loading) return (
    <div data-testid="gallery-manager-page">
      <h2 className="text-2xl font-bold text-gray-900 font-['Kanit'] mb-6">{lang === "es" ? "Galeria" : "Gallery Manager"}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{[1,2,3].map(i => <div key={i} className="h-32 bg-white rounded-2xl animate-pulse" />)}</div>
    </div>
  );

  // Album detail view
  if (selectedAlbum) {
    return (
      <div data-testid="gallery-manager-page">
        <button onClick={() => { setSelectedAlbum(null); setMedia([]); }} data-testid="back-to-albums"
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 text-sm font-medium transition-colors">
          <ArrowLeft className="w-4 h-4" /> {lang === "es" ? "Todos los Albums" : "All Albums"}
        </button>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{selectedAlbum.title}</h2>
            <p className="text-gray-500 text-sm">{selectedAlbum.description || (lang === "es" ? "Sin descripcion" : "No description")}</p>
          </div>
          <button onClick={() => { setEditingMedia(null); setShowMediaForm(true); }} data-testid="add-media-btn"
            className="flex items-center gap-2 bg-[#87CEEB] text-black px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#6bb8d9]">
            <Plus className="w-4 h-4" /> {lang === "es" ? "Agregar Media" : "Add Media"}
          </button>
        </div>

        {media.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
            <Image className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-400">{lang === "es" ? "Album vacio. Agrega fotos o videos!" : "Empty album. Add photos or videos!"}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.map(m => (
              <div key={m.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden group relative" data-testid={`media-item-${m.id}`}>
                {m.type === "image" ? (
                  <img src={m.url} alt={m.title} className="w-full h-36 object-cover" />
                ) : (
                  <div className="w-full h-36 bg-gray-900 flex items-center justify-center relative">
                    {m.url && <video src={m.url} className="w-full h-full object-cover" />}
                    <Play className="absolute w-8 h-8 text-white/80" />
                    {m.youtube_url && (
                      <div className="absolute bottom-1 left-1 bg-red-600 text-white text-xs px-1.5 py-0.5 rounded font-medium">YT</div>
                    )}
                  </div>
                )}
                <div className="p-3">
                  <p className="text-sm font-medium text-gray-800 truncate">{m.title || (m.type === "image" ? "Photo" : "Video")}</p>
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditingMedia(m); setShowMediaForm(true); }}
                    className="p-1.5 bg-white/90 rounded-lg hover:bg-white shadow-sm"><Pencil className="w-3.5 h-3.5 text-gray-600" /></button>
                  <button onClick={() => deleteMedia(m.id)}
                    className="p-1.5 bg-white/90 rounded-lg hover:bg-white shadow-sm"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {showMediaForm && (
          <MediaForm media={editingMedia} albumId={selectedAlbum.id} authFetch={authFetch} lang={lang}
            onSave={saveMedia} onCancel={() => { setShowMediaForm(false); setEditingMedia(null); }} />
        )}
      </div>
    );
  }

  // Albums list view
  return (
    <div data-testid="gallery-manager-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 font-['Kanit']">{lang === "es" ? "Galeria y Albums" : "Gallery & Albums"}</h2>
          <p className="text-gray-500 text-sm">{lang === "es" ? "Gestiona albums, fotos y videos" : "Manage albums, photos, and videos"}</p>
        </div>
        <button onClick={() => { setEditingAlbum(null); setShowAlbumForm(true); }} data-testid="add-album-btn"
          className="flex items-center gap-2 bg-[#87CEEB] text-black px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#6bb8d9]">
          <Plus className="w-4 h-4" /> {lang === "es" ? "Nuevo Album" : "New Album"}
        </button>
      </div>

      {albums.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-16 text-center">
          <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-400">{lang === "es" ? "No hay albums. Crea el primero!" : "No albums yet. Create your first one!"}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {albums.map(a => (
            <div key={a.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all group cursor-pointer"
              onClick={() => setSelectedAlbum(a)} data-testid={`album-card-${a.id}`}>
              <div className="h-40 bg-gray-100 overflow-hidden">
                {a.cover_image ? (
                  <img src={a.cover_image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center"><FolderOpen className="w-12 h-12 text-gray-300" /></div>
                )}
              </div>
              <div className="p-4 flex items-center justify-between">
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 font-['Kanit'] truncate">{a.title}</h3>
                  {a.description && <p className="text-sm text-gray-500 truncate">{a.description}</p>}
                </div>
                <div className="flex gap-1 flex-shrink-0 ml-2">
                  <button onClick={e => { e.stopPropagation(); setEditingAlbum(a); setShowAlbumForm(true); }}
                    className="p-2 hover:bg-amber-50 rounded-lg text-gray-400 hover:text-amber-600"><Pencil className="w-4 h-4" /></button>
                  <button onClick={e => { e.stopPropagation(); deleteAlbum(a.id); }}
                    className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAlbumForm && (
        <AlbumForm album={editingAlbum} lang={lang} onSave={saveAlbum} onCancel={() => { setShowAlbumForm(false); setEditingAlbum(null); }} />
      )}
    </div>
  );
}
